import api, { type RequestOptions } from './api';
import type { Tenant } from './tenant.service';
import { MAX_PAGE_SIZE } from '../utils/constants';

/**
 * Channel service — the middle layer between SuperAdmin and Tenant:
 *
 *     SuperAdmin (global)  >  ChannelAdmin (a set of tenants)  >  TenantAdmin (one tenant)
 *
 * A ChannelAdmin is read-only here: they may list and read the channels they
 * administer, but creating/updating/deleting a channel and appointing its
 * admins are SuperAdmin-only, so a ChannelAdmin cannot widen their own scope.
 * The server enforces all of this — the UI simply doesn't offer the actions.
 */

export interface Channel {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  contactEmail: string | null;
  status: number; // 0 = active, 1 = suspended
  createTime?: string;
  updateTime?: string;
}

export const CHANNEL_STATUS = {
  ACTIVE: 0,
  SUSPENDED: 1,
} as const;

export interface ChannelListParams {
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface ChannelCreateData {
  name: string;
  slug?: string;
  description?: string;
  contactEmail?: string;
  status?: number;
}

export type ChannelUpdateData = Partial<ChannelCreateData>;

export interface PaginatedChannelResponse {
  items: Channel[];
  page: number;
  pageSize: number;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

// SuperAdmin sees every channel; a ChannelAdmin sees only their own. Any other
// user type is rejected with 403 rather than given an empty page.
// `options` exists so the header's scope picker can opt out of the global
// loading overlay — it fetches on every page load.
const listChannels = async (params?: ChannelListParams, options?: RequestOptions) => {
  const response = await api.get('/channels', { params, ...options } as RequestOptions);
  return response.data as PaginatedChannelResponse;
};

const channelService = {
  list: listChannels,

  // Every channel the caller can see, paged out in full. The API caps pageSize
  // at MAX_PAGE_SIZE and 400s above it rather than clamping.
  listAll: async (params?: Omit<ChannelListParams, 'page' | 'pageSize'>, options?: RequestOptions) => {
    const items: Channel[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await listChannels({ ...params, page, pageSize: MAX_PAGE_SIZE }, options);
      items.push(...response.items);
      totalPages = response.pagination?.totalPages ?? 1;
      page += 1;
    } while (page <= totalPages);

    return items;
  },

  getById: async (id: string) => {
    const response = await api.get(`/channels/${id}`);
    return response.data as Channel;
  },

  // SuperAdmin only.
  create: async (data: ChannelCreateData) => {
    const response = await api.post('/channels', data);
    return response.data as Channel;
  },

  // SuperAdmin only.
  update: async (id: string, data: ChannelUpdateData) => {
    const response = await api.put(`/channels/${id}`, data);
    return response.data as Channel;
  },

  // SuperAdmin only. Refused while the channel still owns tenants — the
  // server's message is surfaced to the caller by the api.ts interceptor.
  remove: async (id: string) => {
    await api.delete(`/channels/${id}`);
  },

  // Tenants owned by this channel. SuperAdmin, or a member ChannelAdmin.
  getTenants: async (id: string) => {
    const response = await api.get(`/channels/${id}/tenants`);
    return response.data as Tenant[];
  },

  // SuperAdmin only. The target user must ALREADY be a Channel Administrator
  // (type 5) — this endpoint grants membership, it does not create the user.
  assignAdmin: async (id: string, userId: string) => {
    const response = await api.post(`/channels/${id}/admins`, { userId });
    return response.data;
  },
};

export default channelService;
